import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { getTenantId } from '../../../utils/apiHeaders';
import { getAccessToken } from '../../../utils/token';
import { fetchMyLearnerCurriculum } from '../api/learnerCurriculum.client';
import type { ResolvedLearnerCurriculum } from '../learnerCurriculumTypes';
import {
  isLearnerCurriculumIdentityReady,
  resolveLearnerCurriculumIdentity,
} from '../learnerCurriculumIdentity';
import { learnerCurriculumQueryKey } from '../queryKeys';
import { shouldRetryLearnerCurriculumQuery } from './learnerCurriculumQueryPolicy';

/** Learner curriculum enrollment is stable for most of an active session. */
export const LEARNER_CURRICULUM_STALE_MS = 5 * 60 * 1000;

function resolveCurriculumErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return 'Unable to load your curriculum enrollment';
}

function logLearnerCurriculumDevState(state: {
  identityReady: boolean;
  membershipKey: string;
  platformUserKey: string;
  status: string;
  instructionalTrack?: string | null;
  resolutionSource?: string | null;
  error?: string | null;
}): void {
  if (!import.meta.env.DEV) return;
  console.debug('[learner-curriculum]', state);
}

export function useLearnerCurriculum(): {
  curriculum: ResolvedLearnerCurriculum | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();

  const { membershipKey, platformUserKey, identityReady } = useMemo(() => {
    const tenantId = user?.memberships?.[0]?.school_id ?? getTenantId() ?? null;
    const identity = resolveLearnerCurriculumIdentity({
      tenantId,
      platformUserId: user?.id ?? null,
    });
    const hasSession = Boolean(getAccessToken());
    return {
      ...identity,
      identityReady:
        hasSession &&
        !authLoading &&
        isLearnerCurriculumIdentityReady(identity.membershipKey, identity.platformUserKey),
    };
  }, [authLoading, user?.id, user?.memberships]);

  const queryKey = learnerCurriculumQueryKey({ membershipKey, platformUserKey });

  const query = useQuery({
    queryKey,
    queryFn: fetchMyLearnerCurriculum,
    enabled: identityReady,
    staleTime: LEARNER_CURRICULUM_STALE_MS,
    retry: shouldRetryLearnerCurriculumQuery,
  });

  const curriculum = query.data?.curriculum ?? null;
  const error = query.isError ? resolveCurriculumErrorMessage(query.error) : null;

  logLearnerCurriculumDevState({
    identityReady,
    membershipKey,
    platformUserKey,
    status: !identityReady
      ? 'identity-pending'
      : query.isLoading
        ? 'loading'
        : query.isError
          ? 'error'
          : curriculum?.ok
            ? 'resolved'
            : 'unresolved',
    instructionalTrack: curriculum?.instructional_track ?? null,
    resolutionSource: curriculum?.resolution_source ?? null,
    error,
  });

  return {
    curriculum,
    /**
     * True while auth is bootstrapping or the first in-flight curriculum fetch has not settled.
     * Identity-pending and settled errors are not loading — callers show neutral POC nav instead.
     */
    loading:
      authLoading || (identityReady && query.isLoading && !query.isFetched),
    error,
    reload: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  };
}
